import React, { cloneElement, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Loader2, BadgeDollarSign, MailCheck } from 'lucide-react';

import PublicLayout from '../layouts/PublicLayout';
import Turnstile from '../common/Turnstile';
import { useCreatePartnerSignup } from '../../services/leads';
// Same fixed dark-grey brand theme as the Refer a Veteran form (#38383B page,
// #4A4A4E tile, black fields, white headings, lighter-grey body).
import { INPUT, LABEL, PAGE_BG, useBrandBodyBackground } from './brandTheme';
import { formatUsPhone } from '../../lib/usPhone';

const digits = (v) => (v || '').replace(/\D/g, '');

// Guided US phone input: the shared formatter in src/lib/usPhone.js (it
// started here, 2026-08-31).

// Real <label htmlFor> wired to the input: the id is stamped onto the child so
// the label/input association (a11y + tap-to-focus) comes for free.
const Field = ({ id, label, children }) => (
  <div>
    <label htmlFor={id} className={LABEL}>
      {label}
      <span className="text-rose-400"> *</span>
    </label>
    {cloneElement(children, { id })}
  </div>
);

// Public "Become a Referral Partner" form (logged out) — the DIRECT partner
// signup that doesn't require referring a veteran first. Mints the same
// onboarding token as the /referral opt-in, then drops the person into the
// shared /partner-onboarding/:token flow (W-9 + agreement → graduate to partner).
const PartnerSignup = () => {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });
  const [turnstileToken, setTurnstileToken] = useState('');
  const [done, setDone] = useState(false);
  const turnstileRef = useRef(null);

  useBrandBodyBackground();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setPhone = (e) =>
    setForm((f) => ({ ...f, phone: formatUsPhone(e.target.value) }));

  const signup = useCreatePartnerSignup(
    () => {
      // Email-verify-first: the onboarding link is emailed, not returned inline.
      setDone(true);
    },
    {
      onError: (err) => {
        // Turnstile tokens are single-use — the server consumed this one even on
        // a 4xx, so reset the widget or every retry re-sends the spent token.
        turnstileRef.current?.reset();
        toast.error(
          err?.response?.data?.msg || 'Something went wrong. Please try again.',
        );
      },
    },
  );

  const fieldsComplete =
    form.firstName.trim() &&
    form.lastName.trim() &&
    form.email.trim() &&
    digits(form.phone).length === 10;
  const canSubmit = fieldsComplete && !!turnstileToken && !signup.isLoading;

  const onSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) {
      // When the fields are all fine and only the Turnstile token is missing
      // (unsolved/expired), say so — not "complete your name, email, and phone".
      toast.error(
        fieldsComplete && !turnstileToken
          ? 'Please complete the security check below.'
          : 'Please complete your name, email, and phone number.',
      );
      return;
    }
    signup.mutate({
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      phone: digits(form.phone),
      turnstileToken,
    });
  };

  return (
    <PublicLayout
      background={PAGE_BG}
      footerClassName="text-neutral-300"
      footerVariant="dark"
    >
      <div className="mx-auto max-w-md py-6">
        <div className="rounded-2xl bg-[#4A4A4E] p-6 shadow-lg sm:p-8">
          {done ? (
            <div className="text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-400/10 text-emerald-400">
                <MailCheck className="h-6 w-6" />
              </div>
              <h2 className="text-xl font-bold text-white">Check your email</h2>
              <p className="mt-2 text-sm text-neutral-300">
                We emailed a link to{' '}
                <strong className="text-white">{form.email}</strong> to finish
                becoming a partner — review and e-sign your W-9 + agreement. The
                link is valid for 30 days.
              </p>
            </div>
          ) : (
            <>
              <div className="mb-5 text-center">
                <div className="mx-auto mb-2 flex h-11 w-11 items-center justify-center rounded-full bg-white/10">
                  <BadgeDollarSign className="h-6 w-6 text-white" />
                </div>
                <h2 className="text-xl font-bold text-white">
                  Become a Referral Partner
                </h2>
                <p className="text-center text-sm text-neutral-300">
                  Get your personal referral link and QR code — then earn $250
                  for every veteran you refer who becomes a client and completes
                  their claim.
                </p>
              </div>

              <form onSubmit={onSubmit} className="space-y-4">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Field id="partner-firstName" label="First name">
                    <input
                      className={INPUT}
                      autoComplete="given-name"
                      value={form.firstName}
                      onChange={set('firstName')}
                    />
                  </Field>
                  <Field id="partner-lastName" label="Last name">
                    <input
                      className={INPUT}
                      autoComplete="family-name"
                      value={form.lastName}
                      onChange={set('lastName')}
                    />
                  </Field>
                  <Field id="partner-email" label="Email">
                    <input
                      className={INPUT}
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      value={form.email}
                      onChange={set('email')}
                    />
                  </Field>
                  <Field id="partner-phone" label="Mobile phone">
                    <input
                      className={INPUT}
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      value={form.phone}
                      onChange={setPhone}
                      // No maxLength: the browser would truncate a pasted
                      // "+1 (904) 555-1212" BEFORE formatUsPhone runs and
                      // mangle the exact case this input exists to fix -
                      // the formatter already hard-caps at 10 digits.
                      placeholder="(904) 555-1212"
                    />
                  </Field>
                </div>
                <p className="text-xs text-neutral-300">
                  Next: sign a quick W-9 and partner agreement. Once approved,
                  you&apos;ll be ready to go.
                </p>

                <Turnstile ref={turnstileRef} onToken={setTurnstileToken} />

                <button
                  type="submit"
                  disabled={signup.isLoading}
                  className="inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-white/10 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-neutral-200 hover:!text-black disabled:opacity-50"
                >
                  {signup.isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : null}
                  Continue
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </PublicLayout>
  );
};

export default PartnerSignup;
